import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import debounce from 'lodash.debounce';
import useIsomorphicEffect from '../../../utilities/useIsomorphicEffect';

export const HeightContext = createContext<Heights>({});

const HeightDispatchContext = createContext<React.Dispatch<UpdateHeight>>(
  () => {}
);

interface MatchHeightProviderProps {
  children: React.ReactNode;
  isResponsive?: boolean;
}
interface MatchHeightProps {
  name: string;
  children: React.ReactNode;
}

interface Heights {
  [index: string]: number;
}

interface UpdateHeight {
  name?: string;
  height?: number;
  isReset?: boolean;
}

export const MatchHeightProvider = ({
  children,
  isResponsive,
}: MatchHeightProviderProps): React.ReactElement => {
  const heightReducer = (
    heights: Heights,
    { name, height, isReset }: UpdateHeight
  ) => {
    if (isReset || !(name && height)) {
      return {};
    } else {
      return {
        ...heights,
        [name]: Math.max(height, heights[name] || 0),
      };
    }
  };
  const [heights, updateHeights] = useReducer(heightReducer, {});

  useEffect(() => {
    if (isResponsive) {
      const resizeObserver = new ResizeObserver(
        debounce(() => {
          updateHeights({ isReset: true });
        }, 100)
      );
      resizeObserver.observe(document.body);
      return () => resizeObserver.disconnect();
    }
  }, [isResponsive]);

  return (
    <HeightContext.Provider value={heights}>
      <HeightDispatchContext.Provider value={updateHeights}>
        <div>{children}</div>
      </HeightDispatchContext.Provider>
    </HeightContext.Provider>
  );
};

MatchHeightProvider.defaultProps = {
  isResponsive: false,
};

export const MatchHeight = ({
  name,
  children,
}: MatchHeightProps): React.ReactElement => {
  const heights = useContext(HeightContext);
  const updateHeights = useContext(HeightDispatchContext);
  const ref = useRef<HTMLDivElement>(null);

  const getHeight = () => ref.current?.getBoundingClientRect().height || 0;

  useIsomorphicEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver(
      debounce(() => {
        const minHeight = heights[name] || 0;
        const actualHeight = getHeight();
        if (actualHeight > minHeight) {
          updateHeights({ name, height: actualHeight });
        }
      }, 100)
    );
    resizeObserver.observe(ref.current);
    return () => {
      updateHeights({ name, isReset: true });
      resizeObserver.disconnect();
    };
  }, []);

  useIsomorphicEffect(() => {
    if (!ref.current) return;
    const minHeight = heights[name] || 0;
    const actualHeight = getHeight();
    if (actualHeight > minHeight) {
      updateHeights({ name, height: actualHeight });
    }
  }, [heights[name]]);

  return (
    <div style={{ minHeight: heights[name] || 0 }} ref={ref}>
      {children}
    </div>
  );
};
